import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as api from '../../../../../api/generated';
import { KendoLoading } from '../../../../../components';
import ListItemCreateButton from '../../../../../components/custom/ListItemCreateButton';
import { useFetchedValue } from '../../../../../components/hooks/useFetched';
import { usePageableGrid } from '../../../../../components/hooks/usePageableGrid';
import { Grid, GridColumn } from '../../../../../components/KendoReact/Grid';
import { useWindow } from '../../../../../components/windows/WindowContext';
import { IDoorScreenListItem } from '../../../../../models';
import AdministrationActionCell from '../../components/AdministrationActionCell';
import ConnectionStatusCell from '../components/ConnectionStatusCell';
import DeviceLinkCell from '../components/DeviceLinkCell';
import DisplayTypeCell from '../components/DisplayTypeCell';
import DoorScreenCreateModalWindow from './DoorScreenCreateModalWindow';
import DoorScreenEditModalWindow from './DoorScreenEditModalWindow';

const getDoorScreens = async (organizationInformationId: number, skip: number, take: number) =>
    api.administration.getDoorScreens({
        organizationInformationId,
        skip,
        take
    });

const DeviceDoorScreens: FC = () => {
    const { organizationInformationId } = useParams<'organizationInformationId'>();
    const { openWindow } = useWindow();
    const [gridPageProps] = usePageableGrid([5, 10, 15, 20, 25], 15);
    const [isLoading, setIsLoading] = useState(false);
    const [doorScreens, refetchDoorScreens, , , isDoorScreensLoading] = useFetchedValue(
        getDoorScreens,
        true,
        parseInt(organizationInformationId.toString()),
        gridPageProps.skip,
        gridPageProps.take
    );

    const openDoorScreenEditModalWindow = async (doorScreenId: number) => {
        try {
            DoorScreenEditModalWindow.defaultProps = {
                componentProps: {
                    doorScreenId: doorScreenId,
                    refetchDoorScreens: refetchDoorScreens
                }
            };
            openWindow({
                title: 'Durų ekrano redagavimas',
                component: DoorScreenEditModalWindow,
                isDialogBehaviour: true,
                width: 800,
                height: 400
            });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    const openDoorScreenCreateModalWindow = async (basedOnInitialValues: IDoorScreenListItem | null) => {
        try {
            DoorScreenCreateModalWindow.defaultProps = {
                componentProps: {
                    refetchDoorScreens: refetchDoorScreens,
                    basedOnInitialValues: basedOnInitialValues,
                    openDoorScreenCreateModalWindow: openDoorScreenCreateModalWindow
                }
            };
            openWindow({
                title: 'Durų ekrano kūrimas',
                component: DoorScreenCreateModalWindow,
                isDialogBehaviour: true,
                width: 800,
                height: 400
            });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    const deleteDoorScreen = async (doorScreenId: number) => {
        try {
            setIsLoading(true);
            await api.administration.deleteDoorScreen(doorScreenId);
            toast.success('Įrenginys sėkmingai ištrintas');
        } catch (ex) {
            toast.error('Nepavyko ištrinti info ekrano');
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) return <KendoLoading />;

    return (
        <div>
            <ListItemCreateButton openCreateModal={() => openDoorScreenCreateModalWindow(null)} />
            <Grid className="mt-2" {...gridPageProps} data={doorScreens} isLoading={isDoorScreensLoading}>
                <GridColumn
                    width="200px"
                    title="Veiksmai"
                    cell={(c) => (
                        <AdministrationActionCell
                            {...c}
                            fetchData={refetchDoorScreens}
                            deleteItem={deleteDoorScreen}
                            editItem={openDoorScreenEditModalWindow}
                        />
                    )}
                />
                <GridColumn minResizableWidth={100} width={300} title="Pavadinimas" field="name" />
                <GridColumn minResizableWidth={100} width={150} title="Modelis" field="model" />

                <GridColumn minResizableWidth={50} width={100} title="Statusas" cell={(c) => <ConnectionStatusCell {...c} />} />
                <GridColumn
                    minResizableWidth={130}
                    width={130}
                    title="Atvaizdavimo tipas"
                    cell={(c) => <DisplayTypeCell {...c} device={'doorScreen'} />}
                />
                <GridColumn minResizableWidth={100} width="150px" title="Fizinė lokacija" field="locationName" />
                <GridColumn
                    minResizableWidth={100}
                    title="Įrenginio rodomas vaizdas"
                    cell={(c) => <DeviceLinkCell deviceId={parseInt(c.dataItem.id)} device={'doorScreen'} />}
                />
            </Grid>
        </div>
    );
};

export default DeviceDoorScreens;
